import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { apiFetch } from './client'
import type { ApiError } from './types'

export type APIKey = {
  id: string
  name: string
  prefix: string
  last4: string
  createdAt: string
  lastUsedAt: string | null
}

/** Returned only from POST — contains the raw key that is never shown again. */
export type CreatedAPIKey = APIKey & { key: string }

export function useAPIKeys(): UseQueryResult<APIKey[], ApiError> {
  return useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiFetch<APIKey[]>('/api-keys'),
  })
}

export function useCreateAPIKey(): UseMutationResult<CreatedAPIKey, ApiError, { name: string }> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (data) =>
      apiFetch<CreatedAPIKey>('/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
}

export function useDeleteAPIKey(): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api-keys/${id}`, { method: 'DELETE' }),

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
}
